/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { AIUserMessage } from '../../../../../../ui/organisms/AIUserMessage/AIUserMessage';
import { AIAgentMessage } from '../../../../../../ui/organisms/AIAgentMessage/AIAgentMessage';
import { AILoadingPlaceholder } from '../../../../../../ui/organisms/AILoadingPlaceholder/AILoadingPlaceholder';
import { GeneralActions } from '../../../../../../ui/organisms/AIMessageBlock/GeneralActions';
import { SuggestedActionsBar } from '../SuggestedActionsBar';
import { mapUserFileRefsToAttachments } from '../../../../services/fileMapper';
import { FEEDBACK_TURN_UNKNOWN } from '../../../../constants/feedback';
import { extractTextFromContent, getCopyTextForAgentMessage } from '../../../../helpers/historyHelpers';
import { approvalBlockKey, approvalBlockStatus, collectHistoryApprovalKeys, collectPersistedResponseIds, isApprovalBlock, stripDuplicateApprovalBlocks, suppressLivePersistedMessage } from '../../../../helpers/streamHelpers';
import './ChatMessageListStyles.scss';
const BASE_CLASS = 'ai-chat-message-list';
const SCROLL_TOP_THRESHOLD = 40;
// IP-15374: this predicate used to test `b.type !== 'confirmation'`, which
// silently excluded `execution_plan` — the other approval block type. Every
// execution plan therefore rendered frozen and its "Approve Plan" button was
// permanently disabled in production. Key off the shared APPROVAL_BLOCK_TYPES
// set so a newly-added approval block type is covered here by construction.
function hasActivePendingApproval(message, pendingApprovalId) {
    var _a;
    if (!((_a = message.envelope) === null || _a === void 0 ? void 0 : _a.blocks))
        return false;
    return message.envelope.blocks.some((block) => {
        if (!isApprovalBlock(block))
            return false;
        // Terminal blocks are always frozen regardless of stream state.
        const status = approvalBlockStatus(block);
        const isPending = !status || status === 'pending';
        if (!isPending)
            return false;
        // Rehydration case (no pendingApprovalId): any non-terminal block stays interactive.
        if (!pendingApprovalId)
            return true;
        // Streaming case: live approval id is known — match on the block's own
        // identity field (approval_id for confirmation, plan_id for execution_plan).
        return approvalBlockKey(block) === pendingApprovalId;
    });
}
function feedbackTurnNumber(message) {
    var _a;
    const candidates = [(_a = message.envelope) === null || _a === void 0 ? void 0 : _a.turn_number, message.turn_number];
    const resolved = candidates.find((n) => typeof n === 'number' && Number.isFinite(n) && n >= 1);
    return resolved !== null && resolved !== void 0 ? resolved : FEEDBACK_TURN_UNKNOWN;
}
function extractMessageFileAttachments(message) {
    if (message.file_attachments && message.file_attachments.length > 0) {
        return message.file_attachments;
    }
    if (typeof message.content !== 'object' || message.content === null) {
        return undefined;
    }
    // eslint-disable-next-line camelcase
    const contentWithAttachments = message.content;
    if (Array.isArray(contentWithAttachments.file_attachments) && contentWithAttachments.file_attachments.length > 0) {
        return contentWithAttachments.file_attachments;
    }
    return undefined;
}
export function ChatMessageList({ messages, isStreaming = false, isLoadingMessages = false, liveAgentMessage = null, hasNextPage = false, isFetchingNextPage = false, suggestedActions = [], progressMessage, progressDetail, onFetchNextPage, onSuggestedAction, onBlockAction, onConfirm, onCancel, onUndo, onFeedback, onRetry, pendingApprovalId = null }) {
    var _a, _b, _c, _d, _e, _f;
    const containerRef = useRef(null);
    const prevMessageCountRef = useRef(messages.length);
    const prevFirstIdRef = useRef((_b = (_a = messages[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null);
    const firstMessageId = (_d = (_c = messages[0]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null;
    // Auto-scroll to bottom when new messages arrive or streaming starts.
    // We scroll the container directly rather than using scrollIntoView, because
    // scrollIntoView propagates to ALL ancestor scroll containers — on responses
    // with very tall content (e.g. 100-row tables) this over-scrolls the outer
    // ai-chat-widget__body, pushing the messages panel out of view (IP-14201).
    useEffect(() => {
        const container = containerRef.current;
        if (container)
            container.scrollTop = container.scrollHeight;
    }, [messages.length, isStreaming, liveAgentMessage]);
    // Preserve scroll position after prepending older messages
    useEffect(() => {
        var _a;
        const container = containerRef.current;
        if (!container)
            return;
        const firstId = firstMessageId;
        const wasPrepend = messages.length > prevMessageCountRef.current &&
            prevMessageCountRef.current > 0 &&
            firstId !== prevFirstIdRef.current;
        if (wasPrepend) {
            const newCount = messages.length - prevMessageCountRef.current;
            const anchorId = (_a = messages[newCount]) === null || _a === void 0 ? void 0 : _a.id;
            const anchorEl = anchorId != null ? container.querySelector(`[data-message-id="${anchorId}"]`) : null;
            anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
        prevMessageCountRef.current = messages.length;
        prevFirstIdRef.current = firstId;
    }, [messages.length, firstMessageId]);
    // Detect scroll-to-top for infinite scroll
    const handleScroll = useCallback(() => {
        const container = containerRef.current;
        if (!container || !hasNextPage || isFetchingNextPage)
            return;
        if (container.scrollTop <= SCROLL_TOP_THRESHOLD) {
            onFetchNextPage === null || onFetchNextPage === void 0 ? void 0 : onFetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, onFetchNextPage]);
    // De-duplicate the live message against persisted history (IP-14386).
    // 1. If a persisted copy of this exact response already exists in history
    //    (same response_id, different DB id), drop the whole live copy — this is
    //    the durable-approval *continuation* ("Done." + result card) replayed over
    //    SSE or received live in a second tab, which would otherwise render twice
    //    ("Office Assistant: Done." + "Assistant: Done.").
    // 2. Otherwise, still strip any live approval card whose approval_id/plan_id is
    //    already persisted (the original confirmation-card dedup; the history copy
    //    carries the resolved `status` that disables the buttons).
    // Computed before any early return to keep hook order stable across renders.
    const dedupedLiveMessage = useMemo(() => {
        const live = suppressLivePersistedMessage(liveAgentMessage, collectPersistedResponseIds(messages));
        return stripDuplicateApprovalBlocks(live, collectHistoryApprovalKeys(messages));
    }, [liveAgentMessage, messages]);
    // Skeleton loading per Q-G2
    if (isLoadingMessages) {
        return (React.createElement("div", { className: BASE_CLASS },
            React.createElement("div", { className: `${BASE_CLASS}__loading` },
                React.createElement(AILoadingPlaceholder, { label: "Loading messages..." }))));
    }
    if (messages.length === 0 && !liveAgentMessage && !isStreaming) {
        return React.createElement("div", { className: BASE_CLASS });
    }
    const hasLiveBlocks = !!((_f = (_e = dedupedLiveMessage === null || dedupedLiveMessage === void 0 ? void 0 : dedupedLiveMessage.envelope) === null || _e === void 0 ? void 0 : _e.blocks) === null || _f === void 0 ? void 0 : _f.length);
    const isThinkingState = isStreaming && dedupedLiveMessage && !hasLiveBlocks;
    return (React.createElement("div", { className: BASE_CLASS, ref: containerRef, onScroll: handleScroll },
        isFetchingNextPage && (React.createElement("div", { className: `${BASE_CLASS}__loading` },
            React.createElement(AILoadingPlaceholder, { label: "Loading older messages..." }))),
        messages.map((message) => {
            if (message.role === 'user') {
                const messageAttachments = extractMessageFileAttachments(message);
                const attachments = messageAttachments ? mapUserFileRefsToAttachments(messageAttachments) : undefined;
                const userText = extractTextFromContent(message.content);
                return (React.createElement("div", { key: message.id, "data-message-id": message.id },
                    React.createElement(AIUserMessage, { message: userText, attachments: attachments })));
            }
            if (message.envelope) {
                const isFrozen = !hasActivePendingApproval(message, pendingApprovalId);
                return (React.createElement("div", { key: message.id, "data-message-id": message.id },
                    React.createElement(AIAgentMessage, { envelope: message.envelope, onConfirm: onConfirm, onCancel: onCancel, onAction: onBlockAction, onUndo: onUndo, frozen: isFrozen }),
                    React.createElement(GeneralActions, { responseId: message.envelope.response_id, feedbackId: message.envelope.feedback_id, turnNumber: feedbackTurnNumber(message), agentResponseText: getCopyTextForAgentMessage(message), onFeedback: onFeedback, onRetry: onRetry })));
            }
            return null;
        }),
        isThinkingState && (React.createElement("div", { className: `${BASE_CLASS}__loading` },
            React.createElement(AILoadingPlaceholder, { label: progressMessage || 'Thinking...', detail: progressDetail }))),
        dedupedLiveMessage && hasLiveBlocks && dedupedLiveMessage.envelope && (React.createElement(AIAgentMessage, { key: dedupedLiveMessage.id, envelope: dedupedLiveMessage.envelope, onConfirm: onConfirm, onCancel: onCancel, onAction: onBlockAction, onUndo: onUndo })),
        !isStreaming && suggestedActions.length > 0 && onSuggestedAction && (React.createElement(SuggestedActionsBar, { actions: suggestedActions, onAction: onSuggestedAction }))));
}
